import { matchPath } from 'react-router';

import {
    AISLE_PATH_PREFIX,
    BEST_DEALS_AISLE,
    BUY_IT_AGAIN_AISLE,
    FEATURED_AISLE,
    MEMBER_DEALS_AISLE,
    RESCUED_AISLE,
    SHOP_ALL_AISLE,
    SHOP_WITH_POINTS_AISLE,
} from 'constants/shop';
import { getEncodedPathSegment } from 'utils/pathUtils';

/**
 * getAisleIconName - returns the aisle icon component name for a given aisle name, with optional brand theming
 *
 * @param {string} aisleName – the name of the aisle, derived from /v1/aisles endpoint
 * @param {string} theme='Misfits' - the theme of the desired aisle icon (Imperfect or Misfits)
 * @example
 * const aisleIconName = getAisleIconName('Deli', 'Misfits');
 * @returns {string}
 */
export const getAisleIconName = (aisleName, isMisfitsTheme = true) => {
    switch (aisleName) {
        case 'Curated':
        case 'Featured':
            return 'Featured';
        case 'Shop All':
            return 'ShopAll';
        case 'Fruit':
            return 'Fruit';
        case 'Vegetables':
            return 'Vegetables';
        case 'Meat & Seafood':
            return 'MeatAndSeafood';
        case 'Deli':
            return 'Deli';
        case 'Dairy & Eggs':
            return 'Dairy';
        case 'Our Brands':
            return 'OurBrands';
        case 'Pantry':
        case 'Pasta, Rice & Beans':
            return 'Pantry';
        case 'Canned Goods':
            return 'CannedGoods';
        case 'Pantry Staples':
            return 'PantryStaples';
        case 'Beverages':
            return 'Beverages';
        case 'Wine Shop':
            return 'Wine';
        case 'Bakery':
            return 'Bakery';
        case 'Pet':
            return 'Pet';
        case 'Household':
            return 'Household';
        case 'Gluten-Free':
        case 'Gluten-Free Shop':
            return 'GlutenFree';
        case 'Cold Pack':
            return isMisfitsTheme ? 'MMColdPack' : 'IFColdPack';
        case 'Plant-Based':
        case 'Plant-Based Shop':
            return 'PlantBased';
        case 'Snacks':
            return 'Snacks';
        case 'Produce':
            return isMisfitsTheme ? 'MMProduce' : 'IFProduce';
        case 'Sale':
        case 'Best Deals':
        case 'Black Friday Sale':
        case 'Summer Sale':
            return 'Sale';
        case 'Summer Shop':
            return 'SummerShop';
        case 'Prepared':
        case 'Prepared Foods':
        case 'Meals & Sides':
            return 'PreparedFoods';
        case 'Buy It Again':
            return isMisfitsTheme ? 'MMBuyItAgain' : 'IFBuyItAgain';
        case 'Earth Friendly':
            return 'EarthFriendly';
        case 'Rescued':
        case 'Saved & Rescued':
            return 'Rescued';
        case 'Wellness':
        case 'Vitamins & Supplements':
            return 'VitaminsAndSupplements';
        case 'Member Deals':
            return 'MemberDeals';
        case 'Back to School':
            return 'BackToSchool';
        case 'Spice Up Your Routine':
            return isMisfitsTheme ? 'MMSpiceUpYourRoutine' : 'IFSpiceUpYourRoutine';
        case 'Fall Flavors':
            return isMisfitsTheme ? 'MMFallFlavors' : 'IFFallFlavors';
        case 'Thanksgiving Essentials':
            return isMisfitsTheme ? 'MMThanksgivingEssentials' : 'IFThanksgivingEssentials';
        case 'Holiday Must Haves':
            return isMisfitsTheme ? 'MMHolidayMustHaves' : 'IFHolidayMustHaves';
        case 'New Picks':
        case 'Discover Something New':
            return isMisfitsTheme ? 'MMDiscoverSomethingNew' : 'IFDiscoverSomethingNew';
        case 'Staff Picks':
            return 'StaffPicks';
        case 'Best of Misfits':
            return 'BestOfMisfits';
        case 'Personal Care':
        case 'Health & Beauty':
            return 'PersonalCare';
        case 'Breakfast':
            return 'Breakfast';
        case 'Frozen':
            return 'Frozen';
        case 'Hosting & Entertaining':
            return 'HostingAndEntertaining';
        default:
            return isMisfitsTheme ? 'MMProduce' : 'IFProduce';
    }
};

/**
 * @typedef {Object} Aisle - (impartial typedef, these are just the fields utilized)
 * @property {number} id - The aisle ID
 * @property {string} name - The aisle name
 */

/**
 * @typedef {Object} AisleNav
 * @property {Aisle|undefined}  prevAisle - The previous aisle object (if exists)
 * @property {Aisle}            currentAisle - The current aisle object
 * @property {Aisle|undefined}  nextAisle - The next aisle object (if exists)
 */

/**
 * getPrevAndNextAisles - given an array of "real" aisles, and an optional current aisle index,
 *                        it returns the previous and next aisles for use in navigation
 *
 * @param {Aisle[]} aisles - an array of "real" aisles, doesn't include the Curated/home page
 * @param {number|undefined} currentAisleIndex - 0-based index of current "real" aisle, undefined on Curated/home page
 * @param {boolean|undefined} showBuyItAgainAisle - boolean flag conditioning the display of the "Buy It Again" aisle
 * @param {boolean|undefined} showMemberDealsAisle - boolean flag conditioning the display of the "Member Deals" aisle
 * @returns {AisleNav} - the previous and next aisle objects, if they exist
 */
export function getPrevAndNextAisles(aisles, currentAisleIndex, pathname, showBuyItAgainAisle, showMemberDealsAisle) {
    let prevAisle;
    let currentAisle;
    let nextAisle;

    // currentAisleInde of -1 means we are on a hard-coded aisle
    if (currentAisleIndex === -1) {
        if (matchPath(FEATURED_AISLE.url, pathname)) {
            prevAisle = undefined;
            currentAisle = FEATURED_AISLE;
            nextAisle = showBuyItAgainAisle ? BUY_IT_AGAIN_AISLE : SHOP_WITH_POINTS_AISLE;
        } else if (matchPath(BUY_IT_AGAIN_AISLE.url, pathname)) {
            prevAisle = FEATURED_AISLE;
            currentAisle = BUY_IT_AGAIN_AISLE;
            nextAisle = SHOP_WITH_POINTS_AISLE;
        } else if (matchPath(SHOP_WITH_POINTS_AISLE.url, pathname)) {
            prevAisle = showBuyItAgainAisle ? BUY_IT_AGAIN_AISLE : FEATURED_AISLE;
            currentAisle = SHOP_WITH_POINTS_AISLE;
            nextAisle = showMemberDealsAisle ? MEMBER_DEALS_AISLE : SHOP_ALL_AISLE;
        } else if (matchPath(MEMBER_DEALS_AISLE.url, pathname)) {
            prevAisle = SHOP_WITH_POINTS_AISLE;
            currentAisle = MEMBER_DEALS_AISLE;
            nextAisle = SHOP_ALL_AISLE;
        } else if (matchPath(BEST_DEALS_AISLE.url, pathname)) {
            prevAisle = SHOP_ALL_AISLE;
            currentAisle = BEST_DEALS_AISLE;
            nextAisle = RESCUED_AISLE;
        } else if (matchPath(RESCUED_AISLE.url, pathname)) {
            prevAisle = BEST_DEALS_AISLE;
            currentAisle = RESCUED_AISLE;
            // eslint-disable-next-line prefer-destructuring
            nextAisle = aisles[0];
        } else {
            nextAisle = BEST_DEALS_AISLE;
            currentAisle = SHOP_ALL_AISLE;
            prevAisle = showMemberDealsAisle ? MEMBER_DEALS_AISLE : SHOP_WITH_POINTS_AISLE;
        }
    } else {
        const isFirstAisle = currentAisleIndex === 0;
        const isLastAisle = currentAisleIndex === aisles.length - 1;

        nextAisle = isLastAisle ? undefined : aisles[currentAisleIndex + 1];
        currentAisle = aisles[currentAisleIndex];
        prevAisle = isFirstAisle ? RESCUED_AISLE : aisles[currentAisleIndex - 1];
    }
    return {
        prevAisle,
        currentAisle,
        nextAisle,
    };
}

export function isAisleActive(aisleName, location) {
    return location.pathname.includes(`${AISLE_PATH_PREFIX}/${getEncodedPathSegment(aisleName).toLowerCase()}`);
}

/**
 * Total sellable rows on the Best Deals page. Best Deals aisle API returns
 * `data: { msg, aisles: [{ id, name, rankNumber, items: [...] }, ...] }`.
 */
export function getBestDealsAisleItemCount(data) {
    if (!data?.aisles?.length) return 0;
    return data.aisles.reduce((sum, aisle) => sum + (aisle.items?.length ?? 0), 0);
}

/**
 * Total sellable rows on the rescued page. Rescued-aisle API returns
 * `data: { aisles: [{ name, rankNumber, items: [...] }, ...] }`.
 */
export function getRescuedAisleItemCount(data) {
    if (!data?.aisles?.length) return 0;
    return data.aisles.reduce((sum, aisle) => sum + (aisle.items?.length ?? 0), 0);
}

/**
 * Used for filtering out banners that shouldn't be shown
 *
 * @param {Object} banner - the banner object, initially hard-coded in /src/data/banners.js
 * @param {Array} aisles - an array of shop aisle objects, as returned from useAislesQuery()
 * @param {number} curatedListCount - count of carousels of type LIST
 * @param {boolean} isWineEligible=false - whether the customer is eligible for wine
 * @returns {boolean} - false if the banner should be hidden, else true
 */
export function shouldShowBanner(banner, aisles, curatedListCount, isWineEligible = false) {
    const hideWine = banner.id === 1 && !isWineEligible;
    const oddsAndEndsAisle = aisles?.find((a) => a.name === 'Odds & Ends');
    const hideOddsAndEnds = banner.id === 2 && (!oddsAndEndsAisle || oddsAndEndsAisle.inStockCount === 0);
    return !(hideWine || hideOddsAndEnds);
}

/**
 * Builds a list of breadcrumb data for a given aisle ID.
 * @param {string} [customAisle] - Breadcrumb data for a custom aisle.
 * @param {(string|null)} aisleID - ID of the parent aisle.
 * @param {Object} aisles - Aisles data from the /aisles endpoint.
 * @example
 * // Returns:
 * // [
 * //     { id: 1, title: "Home", url: "/shop" },
 * //     { id: 2, title: "Dairy & Eggs", url: "/shop/aisle/dairy+%26+eggs/all" },
 * //     { id: 3, title: "Milk", url: "/shop/aisle/dairy+%26+eggs/milk" },
 * // ]
 * const aisleBreadcrumbs = getAisleBreadcrumbs({
 *     aisles: [
 *         { id: 123, name: "Vegetables", aisles: [] },
 *         {
 *             id: 456,
 *             name: "Dairy & Eggs",
 *             aisles: [
 *                 { id: 789, name: "Milk", aisles: [] }
 *             ]
 *         },
 *     ],
 *     aisleID: 789,
 * });
 * @returns {number} The percent off original price.
 */
export const getAisleBreadcrumbs = ({ aisleID, aisles = [], customAisle } = {}) => {
    let breadcrumbs = [{ id: 1, title: 'Home', url: '/shop' }];

    if (customAisle) {
        return [...breadcrumbs, { id: 2, ...customAisle }];
    }

    aisles.forEach((aisle) => {
        aisle.aisles.forEach((subAisle) => {
            if (subAisle.id === aisleID) {
                breadcrumbs = [
                    ...breadcrumbs,
                    {
                        id: 2,
                        title: aisle.name,
                        url: `/shop/aisle/${getEncodedPathSegment(aisle.name.toLowerCase())}/all`,
                    },
                    {
                        id: 3,
                        title: subAisle.name,
                        url: `/shop/aisle/${getEncodedPathSegment(aisle.name.toLowerCase())}/${getEncodedPathSegment(
                            subAisle.name.toLowerCase()
                        )}`,
                    },
                ];
            }
        });
        if (aisle.id === aisleID) {
            breadcrumbs = [
                ...breadcrumbs,
                {
                    id: 2,
                    title: aisle.name,
                    url: `/shop/aisle/${getEncodedPathSegment(aisle.name.toLowerCase())}/all`,
                },
            ];
        }
    });

    return breadcrumbs;
};

/**
 * Sorts sold out products to the end of the array.
 * @param {array} products - An array of products with sold out products interspersed
 * @returns {array} An array of products with sold out products at the end of the array
 * const sortedProducts = sortSoldOutProductsLast([
 *      { name: "Cantaloupe", soldOut: true },
 *      { name: "Pink Lady Apples", soldOut: false },
 *      { name: "Asian Pears", soldOut: false },
 *      { name: "Lemons", soldOut: false },
 * ]);
 * // Returns:
 * //   [
 * //     { name: "Pink Lady Apples", soldOut: false },
 * //     { name: "Asian Pears", soldOut: false },
 * //     { name: "Lemons", soldOut: false },
 * //     { name: "Cantaloupe", soldOut: true },
 * //   ]
 */
export const sortSoldOutProductsLast = (products = []) => {
    const oosProducts = [];
    const inStockProducts = [];
    products
        .filter((product) => product)
        .forEach((product) => (product?.soldOut ? oosProducts.push(product) : inStockProducts.push(product)));
    return inStockProducts.concat(oosProducts);
};

/**
 * Measures the combined pixel height of the sticky header and L2 nav bar for scroll padding.
 * @param {boolean} mobile
 * @param {Object} [options]
 * @param {boolean} [options.reserveFullShopHeader] - When true, uses the full #header layout height
 *   instead of only the portion visible in the viewport. Needed when programmatically scrolling
 *   upward on shop: the main nav is hidden while scrolled down but reappears after scroll, and
 *   visible-only measurement would underestimate the offset and leave the aisle header covered.
 * @param {boolean} [options.assumeMainNavHiddenAfterScroll] - When true, treats main nav height as 0.
 *   Used when jumping downward: after the scroll the shop chrome hides the main nav, so using the
 *   currently visible header would leave excess gap above the aisle.
 */
const getStickyOffset = (mobile, { assumeMainNavHiddenAfterScroll = false, reserveFullShopHeader = false } = {}) => {
    if (!mobile) return 160;

    const headerEl = document.getElementById('header');
    const l2NavEl = document.getElementById('l2-nav-wrapper');
    const l2Height = l2NavEl?.offsetHeight || 0;
    const padding = 10;

    let headerChromeHeight;
    if (assumeMainNavHiddenAfterScroll) {
        headerChromeHeight = 0;
    } else if (reserveFullShopHeader) {
        headerChromeHeight = headerEl?.offsetHeight ?? 0;
    } else {
        const headerBottom = headerEl?.getBoundingClientRect().bottom ?? 0;
        headerChromeHeight = Math.max(0, headerBottom);
    }

    return headerChromeHeight + l2Height + padding;
};

/**
 * Scroll to an L2 <section /> element on an aisle page.
 * @param {boolean} mobile - Whether or not the screen is mobile-sized (sm). This is used to calculate necessary offsets.
 * @param {string} selector - A querySelector selector. For example `#aisle-250`
 */
export const scrollToL2AisleElement = ({ mobile = true, selector }) => {
    const aisleElement = document.querySelector(selector);

    if (!aisleElement) return;

    const rect = aisleElement.getBoundingClientRect();
    const elementDocTop = rect.top + window.scrollY;

    let offset;
    if (!mobile) {
        offset = getStickyOffset(false);
    } else {
        const visibleChromeOffset = getStickyOffset(true, { reserveFullShopHeader: false });
        const tentativeScrollTop = elementDocTop - visibleChromeOffset;
        const scrollingUp = tentativeScrollTop < window.scrollY - 2;
        const scrollingDown = tentativeScrollTop > window.scrollY + 2;

        if (scrollingUp) {
            offset = getStickyOffset(true, { reserveFullShopHeader: true });
        } else if (scrollingDown) {
            offset = getStickyOffset(true, { assumeMainNavHiddenAfterScroll: true });
        } else {
            offset = visibleChromeOffset;
        }
    }

    window.scrollTo({
        top: elementDocTop - offset,
    });
};

/**
 * format response from /items endpoint to match data structure
 * of sub aisle with array of all items in aisle
 * @param {Object} response - response from the /items endpoint.
 * @example
 * // Returns:
 * // {
    "msg": "Success",
    "aisle": {
        "id": 283,
        "name": "Meat & Seafood",
        "level": 1
    },
    "items": [
        {
            "id": 416824,
            "name": "Plant-Based Patties, 8 Oz",
            "new": false,
            "price": 5.19,
            "pointPrice": null,
            "msrp": 4.99,
            "soldOut": false,
            "sale": false,
            "exitSku": false,
            "bestSeller": false,
            "maxQuantity": 10,
            "portion": 1,
            "desc": "The Beyond Burger is a plant-based burger that looks, cooks, and satisfies like beef. It has all the juicy, meaty deliciousness of a traditional burger, but comes with the upsides of a plant-based meal. [Description provided by Beyond Meat.]",
            ...
        },
        ...
    ],
    "bundles": [],
    "packGroupID": 1071,
    "subscriptionID": 774204,
    "chargeID": 152142764,
    "customerID": 873583,
    "banners": [],
    "subAisles": [],
    "carousel": null,
    "id": 283,
    "name": "Meat & Seafood"
}
 */
export const handleBuildAllItemsAisle = ({ aisleResponse }) => {
    if (!aisleResponse) return {};

    const { aisle: aisleData, items, subAisles } = aisleResponse?.data ?? {};
    const { id, name } = aisleData ?? {};

    const subAisle = {
        ...aisleResponse.data,
        carousel: null,
        id,
        name,
    };

    let allItems = [];
    if (subAisles?.length) {
        subAisles.map((a) => a.items.forEach((i) => allItems.push(i)));
    } else {
        allItems = items;
    }

    // filter out duplicate items in the same subaisle
    const allItemsFiltered = allItems?.reduce((acc, currItem) => {
        if (!acc.some((item) => item.id === currItem.id)) {
            acc.push(currItem);
        }
        return acc;
    }, []);

    subAisle.items = allItemsFiltered;
    return subAisle;
};

/**
 * Mobile L1 strip: Featured, Shop All, Best Deals, Rescued (order is product-facing), then API aisles.
 * Desktop "Aisles" column uses {@link buildDesktopAisleSectionList} (Featured is in curated column).
 *
 * @param {Array} dynamicAisles — aisles from `/v1/aisles` (or equivalent)
 */
export function buildMobileL1AisleList(dynamicAisles = []) {
    return [FEATURED_AISLE, SHOP_ALL_AISLE, BEST_DEALS_AISLE, RESCUED_AISLE, ...dynamicAisles];
}

/**
 * Desktop left nav "Aisles" block: Shop All, Best Deals, and Rescued (with counts), then dynamic aisles.
 *
 * @param {Array} dynamicAisles
 * @param {number} inStockAllItemsCount
 * @param {number} bestDealsInStockCount
 * @param {number} [rescuedInStockCount=0]
 */
export function buildDesktopAisleSectionList(
    dynamicAisles,
    inStockAllItemsCount,
    bestDealsInStockCount,
    rescuedInStockCount = 0
) {
    return [
        { ...SHOP_ALL_AISLE, inStockCount: inStockAllItemsCount },
        { ...BEST_DEALS_AISLE, inStockCount: bestDealsInStockCount },
        { ...RESCUED_AISLE, inStockCount: rescuedInStockCount },
        ...dynamicAisles,
    ];
}

/**
 * Desktop left nav "Curated" block — single ordering for Featured, optional Buy It Again, Shop with Points, member deals.
 *
 * @param {object} options
 * @param {boolean} options.showBuyItAgain — Buy It Again row (after previous-purchase data has loaded and is non-empty)
 * @param {boolean} options.showShopWithPoints — Shop with Points row (after previous-purchase query has finished loading)
 * @param {string} options.plusMembershipBranding — e.g. "Misfits+" / "Imperfect+"
 * @returns {Array<{ id: string, name: string, url: string, navLinkEnd?: boolean }>}
 */
export function getDesktopCuratedNavItems({ plusMembershipBranding, showBuyItAgain, showShopWithPoints }) {
    const memberDeals = {
        ...MEMBER_DEALS_AISLE,
        name: `${plusMembershipBranding} Deals`,
    };

    const items = [{ ...FEATURED_AISLE, navLinkEnd: true }];

    if (showBuyItAgain) {
        items.push(BUY_IT_AGAIN_AISLE);
    }

    if (showShopWithPoints) {
        // Preserve historical analytics casing (display name stays from constant)
        items.push({ ...SHOP_WITH_POINTS_AISLE, analyticsAisleName: 'Shop With Points' });
    }

    items.push(memberDeals);

    return items;
}
